import { useCallback, useEffect, useState } from 'react';
import type { JournalDto, ManualJournalSummary } from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import { formatMoney } from './money';
import Loading from './Loading';
import Hint from './Hint';

interface Props {
  companyId: string;
  /** يتغير بعد كل ترحيل فتُعاد القراءة. */
  reloadKey: number;
  /** قيد اتعكس بغرض التعديل — سطوره تُنسخ فى النموذج ليُرحَّل مُصحَّحًا. */
  onEdit: (journal: JournalDto) => void;
  onReversed: () => void;
}

const ISSUE_MESSAGES: Record<string, string> = {
  ALREADY_REVERSED: 'القيد ده اتعكس قبل كده.',
  NOT_MANUAL_JOURNAL: 'ده قيد مستند — يتلغى من شاشة المستند نفسه.',
  PERIOD_CLOSED: 'الفترة المالية بتاعة القيد مقفولة — افتحها الأول أو اعكس بتاريخ فترة مفتوحة.',
  DATE_OUTSIDE_PERIOD: 'تاريخ العكس مش فى فترة مالية.',
  NEGATIVE_CASHBOX: 'العكس هيخلّى الخزينة بالسالب.',
};

function messageOf(caught: unknown): string {
  if (caught instanceof ProblemError) {
    const issues = caught.problem.issues ?? [];
    if (issues.length) return issues.map((i) => ISSUE_MESSAGES[i.code] ?? i.message).join(' ');
    return caught.problem.detail ?? caught.problem.title;
  }
  return caught instanceof Error ? caught.message : String(caught);
}

/**
 * القيود اليدوية المرحّلة. القيد المنشور لا يُمسح ولا يُعدَّل فى مكانه: «عكس» يرحّل قيدًا
 * مقلوبًا بنفس التاريخ، و«تعديل» يعكس ثم يفتح نسخة فى النموذج للتصحيح — فالدفتر يحفظ
 * الأصل والعكس والتصحيح، وسجل التدقيق يحفظ مين عمل إيه.
 */
export default function ManualJournals({ companyId, reloadKey, onEdit, onReversed }: Props) {
  const [journals, setJournals] = useState<ManualJournalSummary[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .manualJournals(companyId)
      .then(setJournals)
      .catch((caught: unknown) => {
        if (!(caught instanceof UnauthorizedError)) setFailure(messageOf(caught));
      });
  }, [companyId]);

  useEffect(load, [load, reloadKey]);

  async function reverse(journal: ManualJournalSummary, forEdit: boolean): Promise<void> {
    const reason = window.prompt(
      (forEdit ? 'تعديل' : 'عكس') + ' القيد ' + (journal.number ?? '') + ' — اكتب السبب:',
      forEdit ? 'تصحيح' : '',
    );
    if (reason === null) return;
    if (reason.trim().length < 3) {
      setFailure('اكتب سبب من ٣ حروف على الأقل.');
      return;
    }
    setBusyId(journal.id);
    setFailure(null);
    try {
      // العكس بتاريخ القيد نفسه: أرقام فترته ترجع زى ما كانت قبله
      await api.reverseJournal(companyId, journal.id, crypto.randomUUID(), {
        reversalDate: journal.postingDate,
        reason: reason.trim(),
      });
      if (forEdit) onEdit(await api.journal(companyId, journal.id));
      onReversed();
      load();
    } catch (caught) {
      setFailure(messageOf(caught));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="panel">
      <h2>القيود اليدوية المرحّلة</h2>
      <Hint block>
        «عكس» بيرحّل قيد مقلوب بنفس التاريخ فيلغى أثر القيد. «تعديل» بيعكسه ويفتحلك نسخة منه فوق
        تصحّحها وترحّلها. القيد الأصلى والعكس بيفضلوا ظاهرين فى الدفاتر.
      </Hint>
      {failure && <p className="error">{failure}</p>}
      {journals === null && !failure && <Loading />}
      {journals?.length === 0 && <p className="sub">لا قيود يدوية بعد.</p>}
      {journals && journals.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>رقم القيد</th>
                <th>التاريخ</th>
                <th>المرجع</th>
                <th>البيان</th>
                <th>المبلغ</th>
                <th>الحالة</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {journals.map((journal) => (
                <tr key={journal.id}>
                  <td className="mono">{journal.number}</td>
                  <td className="mono">{journal.postingDate}</td>
                  <td>{journal.reference ?? ''}</td>
                  <td>{journal.memo ?? ''}</td>
                  <td className="num-cell">{formatMoney(journal.totalDebit)}</td>
                  <td>
                    {journal.reversedBy ? (
                      <span className="pill bad">معكوس بـ {journal.reversedBy.number}</span>
                    ) : (
                      <span className="pill">مرحّل</span>
                    )}
                  </td>
                  <td>
                    {!journal.reversedBy && (
                      <div className="panel-actions">
                        <button
                          type="button"
                          className="ghost"
                          disabled={busyId === journal.id}
                          onClick={() => void reverse(journal, true)}
                        >
                          تعديل
                        </button>
                        <button
                          type="button"
                          className="ghost"
                          disabled={busyId === journal.id}
                          onClick={() => void reverse(journal, false)}
                        >
                          عكس
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
